import { z } from 'zod';

import { CoachGrade, coachGradeSchema } from '../coach-grades.js';
import { CSVUploadResponse } from '../csv.js';
import { ClassEligibility, classEligibilitySchema } from './coach-availability-shared.js';
import {
  BackgroundCheckStatus,
  BackgroundCheckStatusLabelMap,
  ClassEligibilityLabelMap,
  CoachStatus,
  CoachStatusLabelMap,
  CoachStatusTree,
  CoachSubStatus,
  CoachSubStatusLabelMap,
  coachStatusSchema,
  coachSubStatusSchema,
} from './coach.js';
import { backgroundCheckStatusSchema } from './coach-shared.js';

//#region Column definitions (single source of truth)

type CoachCsvColumnDef = {
  readonly key: string;
  readonly label: string;
  readonly matchKey?: true;
  readonly createRequired?: true;
  readonly clearable?: true;
  readonly booleanColumn?: true;
};

/** Canonical CSV column metadata — keep in sync with the Google Sheet template. */
const COACH_CSV_COLUMN_DEFS = [
  { key: 'email', label: 'Email', matchKey: true, createRequired: true },
  { key: 'first_name', label: 'First name', createRequired: true },
  { key: 'last_name', label: 'Last name', createRequired: true },
  { key: 'timezone', label: 'Timezone' },
  { key: 'phone', label: 'Phone' },
  { key: 'displayName', label: 'Display name' },
  { key: 'secondaryEmail', label: 'Secondary email', clearable: true },
  { key: 'zoomPersonalMeetingUrl', label: 'Zoom meeting URL', clearable: true },
  { key: 'JPSGoogleMeetUrl', label: 'JPS Google Meet URL', clearable: true },
  { key: 'airReadingGoogleMeetUrl', label: 'Air Reading Google Meet URL', clearable: true },
  { key: 'zoomTechCheck', label: 'Zoom tech check', booleanColumn: true },
  { key: 'googleMeetTechCheck', label: 'Google Meet tech check', booleanColumn: true },
  { key: 'backgroundCheckStatuses', label: 'Background check statuses' },
  { key: 'classEligibilities', label: 'Class eligibilities' },
  { key: 'status', label: 'Teacher status' },
  { key: 'subStatus', label: 'Teacher sub-status' },
  { key: 'qualityGrade', label: 'Quality grade' },
  { key: 'demoGrade', label: 'Demo grade' },
  { key: 'behaviorNotes', label: 'Behavior notes', clearable: true },
  { key: 'isTrial', label: 'Is trial', booleanColumn: true },
  { key: 'floridaTutorId', label: 'Florida tutor ID', clearable: true },
  { key: 'historicalReviewNotes', label: 'Historical review notes', clearable: true },
] as const satisfies readonly CoachCsvColumnDef[];

type CoachCsvColumnKey = (typeof COACH_CSV_COLUMN_DEFS)[number]['key'];
type CoachCsvNullableColumnKey = Exclude<CoachCsvColumnKey, 'email'>;

function coachCsvColumnKeysWhere(predicate: (def: CoachCsvColumnDef) => boolean): readonly CoachCsvColumnKey[] {
  return COACH_CSV_COLUMN_DEFS.filter(predicate).map((def) => def.key);
}

/** Canonical CSV header strings — derived from {@link COACH_CSV_COLUMN_DEFS}. */
export const CoachCsvColumns = Object.fromEntries(COACH_CSV_COLUMN_DEFS.map((def) => [def.key, def.key])) as {
  readonly [K in CoachCsvColumnKey]: K;
};

export type CoachCsvColumn = CoachCsvColumnKey;

export const COACH_CSV_MATCH_KEY_COLUMNS = coachCsvColumnKeysWhere((def) => def.matchKey === true);
export const COACH_CSV_CREATE_REQUIRED_COLUMNS = coachCsvColumnKeysWhere((def) => def.createRequired === true);
export const COACH_CSV_OPTIONAL_COLUMNS = coachCsvColumnKeysWhere((def) => def.createRequired !== true);
export const COACH_CSV_CLEARABLE_COLUMNS = coachCsvColumnKeysWhere((def) => def.clearable === true);

export type CoachCsvClearableColumn = (typeof COACH_CSV_CLEARABLE_COLUMNS)[number];

/** Columns that must never appear in the CSV template or be written by import. */
export const COACH_CSV_EXCLUDED_COLUMNS = [
  'smsReminderOptedIn',
  'zoomPersonalMeetingId',
  'zoomType',
  'attendanceGrade',
  'vendorId',
  'paymentInstrumentId',
  'paymentAccountType',
  'accountHolderName',
] as const;
export type CoachCsvExcludedColumn = (typeof COACH_CSV_EXCLUDED_COLUMNS)[number];

export const COACH_CSV_ALL_KNOWN_COLUMNS = COACH_CSV_COLUMN_DEFS.map((def) => def.key);

/** Text/free-form columns scanned for suspicious pasted content (excludes yes/no boolean columns). */
export const COACH_CSV_DANGER_FIELDS = coachCsvColumnKeysWhere((def) => def.booleanColumn !== true);

export const COACH_CSV_FIELD_LABELS: Record<string, string> = {
  ...Object.fromEntries(COACH_CSV_COLUMN_DEFS.map((def) => [def.key, def.label])),
  zoomPersonalMeetingId: 'Zoom meeting ID',
};

export const PostCoachUsersCSVFormKeys = {
  coachesCSV: 'coachesCSV',
} as const;

//#endregion

//#region Clearable semantics

export const COACH_CSV_CLEAR_VALUE = 'CLEAR' as const;

export type CoachCsvFieldUpdate<T> = { kind: 'unchanged' } | { kind: 'set'; value: T } | { kind: 'clear' };

export type ParseCoachCsvFieldUpdateResult<T> =
  | { ok: true; update: CoachCsvFieldUpdate<T> }
  | { ok: false; message: string };

export function isCoachCsvClearableColumn(column: string): column is CoachCsvClearableColumn {
  return (COACH_CSV_CLEARABLE_COLUMNS as readonly string[]).includes(column);
}

export function isCoachCsvExcludedColumn(column: string): column is CoachCsvExcludedColumn {
  return (COACH_CSV_EXCLUDED_COLUMNS as readonly string[]).includes(column);
}

/** Returns the subset of known coach CSV columns present in a parsed file header row. */
export function coachCsvPresentColumnsFromHeaders(headers: readonly string[]): Set<CoachCsvColumn> {
  const known = new Set<string>(COACH_CSV_ALL_KNOWN_COLUMNS);
  return new Set(headers.filter((header): header is CoachCsvColumn => known.has(header)));
}

export function coachCsvUnsupportedHeadersFrom(headers: readonly string[]): string[] {
  const supportedHeaders = new Set<string>(COACH_CSV_ALL_KNOWN_COLUMNS);
  return headers.filter((header) => !supportedHeaders.has(header));
}

/**
 * Interprets a single CSV cell according to update semantics:
 * - omitted column: unchanged
 * - blank cell: unchanged
 * - CLEAR: clear (only when clearable)
 * - any other value: set
 */
export function parseCoachCsvFieldUpdate<T = string>(args: {
  columnPresent: boolean;
  rawValue: string | null | undefined;
  clearable: boolean;
}): ParseCoachCsvFieldUpdateResult<T> {
  if (!args.columnPresent) {
    return { ok: true, update: { kind: 'unchanged' } };
  }

  const trimmed = typeof args.rawValue === 'string' ? args.rawValue.trim() : '';

  if (trimmed === '') {
    return { ok: true, update: { kind: 'unchanged' } };
  }

  if (trimmed.toUpperCase() === COACH_CSV_CLEAR_VALUE) {
    if (!args.clearable) {
      return {
        ok: false,
        message: `The value ${COACH_CSV_CLEAR_VALUE} is not supported for this column`,
      };
    }

    return { ok: true, update: { kind: 'clear' } };
  }

  return { ok: true, update: { kind: 'set', value: trimmed as T } };
}

//#endregion

//#region Raw row schema

const coachCsvNullableCellSchema = z
  .union([z.string(), z.literal(''), z.null(), z.undefined()])
  .transform((value): string | null => {
    if (value == null) {
      return null;
    }

    const trimmed = value.trim();
    return trimmed === '' ? null : trimmed;
  });

const coachCsvEmailCellSchema = z
  .union([z.string(), z.literal(''), z.null(), z.undefined()])
  .transform((value): string => {
    if (value == null) {
      return '';
    }

    return value.trim().toLowerCase();
  })
  .pipe(z.string().min(1, 'Email is required').email('Invalid email'));

const coachCsvRawRowShape = Object.fromEntries(
  COACH_CSV_COLUMN_DEFS.map((def) => [
    def.key,
    def.key === 'email' ? coachCsvEmailCellSchema : coachCsvNullableCellSchema.optional(),
  ]),
) as {
  [K in CoachCsvColumnKey]: K extends 'email'
    ? typeof coachCsvEmailCellSchema
    : z.ZodOptional<typeof coachCsvNullableCellSchema>;
};

export type CoachCsvRawRow = {
  email: string;
} & {
  [K in CoachCsvNullableColumnKey]: string | null;
};

function withCoachCsvNullDefaults(row: z.output<z.ZodObject<typeof coachCsvRawRowShape>>): CoachCsvRawRow {
  const result = { email: row.email } as CoachCsvRawRow;

  for (const def of COACH_CSV_COLUMN_DEFS) {
    if (def.key === 'email') {
      continue;
    }

    result[def.key] = row[def.key] ?? null;
  }

  return result;
}

/**
 * Loose row schema for upsert CSV import.
 * Only email is always required; create-only required fields are validated in server transform.
 */
export const coachCsvRawRowSchema = z.object(coachCsvRawRowShape).transform(withCoachCsvNullDefaults);

//#endregion

//#region Shared parsers

export const coachCsvBooleanSchema = z.union([
  z.literal('yes').transform(() => true),
  z.literal('no').transform(() => false),
]);

export const ZOOM_MEETING_ID_MIN_LENGTH = 9;
export const ZOOM_MEETING_ID_MAX_LENGTH = 11;
export const zoomMeetingIdExtractRegex = /j\/(\d+)/;

export type ExtractZoomMeetingIdResult = { ok: true; meetingId: string } | { ok: false; message: string };

/**
 * Ensures a phone number has the "+" country code prefix required in V2.
 */
export function normalizePhoneForV2(phone: string): string {
  const trimmed = phone.trim();
  if (!trimmed) {
    return trimmed;
  }

  if (trimmed.startsWith('+')) {
    return trimmed;
  }

  if (/\d/.test(trimmed)) {
    return `+${trimmed}`;
  }

  return trimmed;
}

export function extractZoomMeetingIdFromUrl(url: string): ExtractZoomMeetingIdResult {
  const trimmed = url.trim();
  if (!trimmed) {
    return { ok: false, message: 'Zoom meeting URL is required' };
  }

  const match = zoomMeetingIdExtractRegex.exec(trimmed);
  const extractedId = match?.[1] ?? '';
  const isValid =
    extractedId.length >= ZOOM_MEETING_ID_MIN_LENGTH &&
    extractedId.length <= ZOOM_MEETING_ID_MAX_LENGTH &&
    /^\d+$/.test(extractedId);

  if (!isValid) {
    return {
      ok: false,
      message: 'The Zoom Meeting ID should be a number between 9 and 11 digits',
    };
  }

  return { ok: true, meetingId: extractedId };
}

export function parseCoachCsvCommaSeparatedList(raw: string): string[] {
  return raw
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
}

const BACKGROUND_CHECK_CSV_LABELS: Record<string, BackgroundCheckStatus> = {
  checkr_passed: BackgroundCheckStatus.checkr_passed,
  'checkr passed': BackgroundCheckStatus.checkr_passed,
  'Checkr Passed': BackgroundCheckStatus.checkr_passed,
  ebr_fingerprint_passed: BackgroundCheckStatus.ebr_fingerprint_passed,
  'ebr fingerprint passed': BackgroundCheckStatus.ebr_fingerprint_passed,
  'EBR Background Check - Cleared Spring/Summer 2025': BackgroundCheckStatus.ebr_fingerprint_passed,
  ebr_fingerprint_quality_reject: BackgroundCheckStatus.ebr_fingerprint_quality_reject,
  'ebr fingerprint quality rejection': BackgroundCheckStatus.ebr_fingerprint_quality_reject,
  'Louisiana Fingerprint quality rejection': BackgroundCheckStatus.ebr_fingerprint_quality_reject,
  california_fingerprint_passed: BackgroundCheckStatus.california_fingerprint_passed,
  'california fingerprint passed': BackgroundCheckStatus.california_fingerprint_passed,
  'California Fingerprint Passed': BackgroundCheckStatus.california_fingerprint_passed,
  florida_fingerprint_passed: BackgroundCheckStatus.florida_fingerprint_passed,
  'florida fingerprint passed': BackgroundCheckStatus.florida_fingerprint_passed,
  'Florida Fingerprint Passed': BackgroundCheckStatus.florida_fingerprint_passed,
  florida_foundations_course_completed: BackgroundCheckStatus.florida_foundations_course_completed,
  'florida foundations course completed': BackgroundCheckStatus.florida_foundations_course_completed,
  'Florida Foundations Course Completed': BackgroundCheckStatus.florida_foundations_course_completed,
  uva_cleared: BackgroundCheckStatus.uva_cleared,
  'uva cleared': BackgroundCheckStatus.uva_cleared,
  'UVA Cleared': BackgroundCheckStatus.uva_cleared,
  louisiana_can: BackgroundCheckStatus.louisiana_can,
  'louisiana can': BackgroundCheckStatus.louisiana_can,
};

const CLASS_ELIGIBILITY_CSV_LABELS: Record<string, ClassEligibility> = {
  can_teach_intervention: ClassEligibility.can_teach_intervention,
  'can teach intervention': ClassEligibility.can_teach_intervention,
  can_teach_diagnostic: ClassEligibility.can_teach_diagnostic,
  'can teach diagnostic': ClassEligibility.can_teach_diagnostic,
  can_teach_practice: ClassEligibility.can_teach_practice,
  'can teach practice': ClassEligibility.can_teach_practice,
  air_crew: ClassEligibility.air_crew,
  'air crew': ClassEligibility.air_crew,
  imported_from_v1: ClassEligibility.imported_from_v1,
  imported: ClassEligibility.imported_from_v1,
};

const COACH_STATUS_CSV_LABELS = Object.fromEntries(
  Object.entries(CoachStatusLabelMap).map(([value, label]) => [label, value as CoachStatus]),
) as Record<string, CoachStatus>;

const COACH_SUB_STATUS_CSV_LABELS = Object.fromEntries(
  Object.entries(CoachSubStatusLabelMap).map(([value, label]) => [label, value as CoachSubStatus]),
) as Record<string, CoachSubStatus>;

const BACKGROUND_CHECK_DISPLAY_LABELS = Object.fromEntries(
  Object.entries(BackgroundCheckStatusLabelMap).map(([enumValue, label]) => [
    label,
    enumValue as BackgroundCheckStatus,
  ]),
) as Record<string, BackgroundCheckStatus>;

const CLASS_ELIGIBILITY_DISPLAY_LABELS = Object.fromEntries(
  Object.entries(ClassEligibilityLabelMap).map(([enumValue, label]) => [label, enumValue as ClassEligibility]),
) as Record<string, ClassEligibility>;

function lookupByNormalizedLabel<T extends string>(labels: Record<string, T>, raw: string): T | null {
  const direct = labels[raw];
  if (direct) {
    return direct;
  }

  const normalized = raw.trim().toLowerCase();
  for (const [label, value] of Object.entries(labels)) {
    if (label.toLowerCase() === normalized) {
      return value;
    }
  }

  return null;
}

export function coachCsvQualityGradeFrom(raw: string): CoachGrade {
  const normalized = raw.trim();
  if (normalized.toLowerCase() === 'pending') {
    return 'New';
  }

  return coachGradeSchema.parse(normalized);
}

export function coachCsvCoachStatusFrom(raw: string): CoachStatus {
  const trimmed = raw.trim();
  if ((coachStatusSchema.options as readonly string[]).includes(trimmed)) {
    return coachStatusSchema.parse(trimmed);
  }

  const fromLabel = lookupByNormalizedLabel(COACH_STATUS_CSV_LABELS, trimmed);
  if (fromLabel) {
    return fromLabel;
  }

  throw new TypeError(`Unrecognized teacher status value "${raw}"`);
}

export function coachCsvCoachSubStatusFrom(raw: string): CoachSubStatus {
  const trimmed = raw.trim();
  if ((coachSubStatusSchema.options as readonly string[]).includes(trimmed)) {
    return coachSubStatusSchema.parse(trimmed);
  }

  const fromLabel = lookupByNormalizedLabel(COACH_SUB_STATUS_CSV_LABELS, trimmed);
  if (fromLabel) {
    return fromLabel;
  }

  throw new TypeError(`Unrecognized teacher sub-status value "${raw}"`);
}

export function coachCsvBackgroundCheckStatusesFrom(raw: string): BackgroundCheckStatus[] {
  const values = parseCoachCsvCommaSeparatedList(raw);
  const parsed: BackgroundCheckStatus[] = [];

  for (const value of values) {
    const fromKnownLabel = lookupByNormalizedLabel(BACKGROUND_CHECK_CSV_LABELS, value);
    if (fromKnownLabel) {
      parsed.push(fromKnownLabel);
      continue;
    }

    const fromDisplayLabel = lookupByNormalizedLabel(BACKGROUND_CHECK_DISPLAY_LABELS, value);
    if (fromDisplayLabel) {
      parsed.push(fromDisplayLabel);
      continue;
    }

    parsed.push(backgroundCheckStatusSchema.parse(value));
  }

  return parsed;
}

export function coachCsvClassEligibilitiesFrom(raw: string): ClassEligibility[] {
  const values = parseCoachCsvCommaSeparatedList(raw);
  const parsed: ClassEligibility[] = [];

  for (const value of values) {
    const fromKnownLabel = lookupByNormalizedLabel(CLASS_ELIGIBILITY_CSV_LABELS, value);
    if (fromKnownLabel) {
      parsed.push(fromKnownLabel);
      continue;
    }

    const fromDisplayLabel = lookupByNormalizedLabel(CLASS_ELIGIBILITY_DISPLAY_LABELS, value);
    if (fromDisplayLabel) {
      parsed.push(fromDisplayLabel);
      continue;
    }

    parsed.push(classEligibilitySchema.parse(value));
  }

  return parsed;
}

export function coachCsvBooleanFrom(raw: string): boolean {
  return coachCsvBooleanSchema.parse(raw.trim().toLowerCase());
}

export function assertCoachCsvStatusSubStatusPair(status: CoachStatus, subStatus: CoachSubStatus | null): void {
  if (subStatus == null) {
    return;
  }

  const allowedSubStatuses = CoachStatusTree[status];
  if (!allowedSubStatuses.includes(subStatus)) {
    throw new TypeError(`Sub-status "${subStatus}" is not valid for status "${status}"`);
  }
}

//#endregion

//#region Dry-run / apply types

export type CoachCsvFieldChange = {
  field: string;
  current: string | null;
  proposed: string | null;
};

export type CoachCsvCreateDelta = {
  kind: 'create';
  row: number;
  email: string;
  displayName: string;
  values: Record<string, string | null>;
};

export type CoachCsvUpdateDelta = {
  kind: 'update';
  row: number;
  email: string;
  userId: string;
  changes: CoachCsvFieldChange[];
};

export type CoachCsvFailure = {
  kind: 'fail';
  row: number;
  email?: string;
  message: string;
};

export type CoachCsvSkip = {
  kind: 'skip';
  row: number;
  email: string;
  reason: string;
};

export type CoachCsvApplySummary = {
  creates: CoachCsvCreateDelta[];
  updates: CoachCsvUpdateDelta[];
  failures: CoachCsvFailure[];
  skips: CoachCsvSkip[];
};

export type CoachCsvDryRunResult = CoachCsvApplySummary & {
  previewToken: string | null;
  totals: {
    create: number;
    update: number;
    fail: number;
    skip: number;
  };
};

export function coachCsvTotalsFromSummary(
  summary: Pick<CoachCsvApplySummary, 'creates' | 'updates' | 'failures' | 'skips'>,
): CoachCsvDryRunResult['totals'] {
  return {
    create: summary.creates.length,
    update: summary.updates.length,
    fail: summary.failures.length,
    skip: summary.skips.length,
  };
}

export function coachCsvDryRunResultFromSummary(
  summary: CoachCsvApplySummary,
  previewToken: string | null = null,
): CoachCsvDryRunResult {
  return {
    ...summary,
    previewToken,
    totals: coachCsvTotalsFromSummary(summary),
  };
}

export type UpsertCoachUsersCsvResult = CSVUploadResponse<{
  createdRowsCount: number;
  updatedRowsCount: number;
  skippedRowsCount?: number;
  summary?: CoachCsvApplySummary;
}>;

//#endregion
